import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { LoadingGroup, LoadingSkeleton, Message, Messages, Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold } from "capitalrx-components-2.0/lib/typography";
import { formatDate, formatDollars } from "capitalrx-utilities";

import { useGetTotalMedicarePrescriptionPaymentPlanTransactions } from "@/App/api/medicarePrescriptionPaymentPlan";
import { useCalculateMemberMedicarePrescriptionPaymentPlanAccumulationBalances } from "@/App/api/member";
import { ClientAwareLink } from "@/App/components";
import { ExperienceContext } from "@/App/context";
import { useIsLoggedInUser } from "@/App/hooks";
import { usePlanYear } from "@/App/pages";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.participationAccumulation";

export const AccumulationPanel: FC = () => {
  const now = new Date();

  const { t: translate } = useTranslation();
  const { hideMemberClaimsPage } = useContext(ExperienceContext);
  const { isLoggedInUser } = useIsLoggedInUser();
  const planYear = usePlanYear();

  const {
    data: transactionsTotal,
    isLoading: isTotalTransactionsLoading,
    isError: isTotalTransactionsError,
  } = useGetTotalMedicarePrescriptionPaymentPlanTransactions(planYear);

  const {
    data: memberAccumulations,
    isLoading: isLoadingAccums,
    isError: isAccumsError,
  } = useCalculateMemberMedicarePrescriptionPaymentPlanAccumulationBalances(true, planYear);

  return (
    <Stack>
      <Heading>
        <Shelf>
          {translate(`${TRANSLATION_PATH}.yearToDateBalance`)}
          <Tag type="info" style={{ display: "inline-flex", alignItems: "center" }}>
            {planYear}
          </Tag>
          <Shelf.Spacer />
        </Shelf>
      </Heading>
      <Shelf>
        <Stack spacing="none">
          <Semibold>
            {translate(`${TRANSLATION_PATH}.accumulated`)} ({translate(`${TRANSLATION_PATH}.asOf`)} {formatDate(now, "MM/DD/YYYY")}):
          </Semibold>
          <LoadingGroup loading={isLoadingAccums}>
            <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 150, height: 20 }} />}>
              <Message visible={isAccumsError} appearance={Messages.Error}>
                {translate(`${TRANSLATION_PATH}.noAccumulationAmountLoaded`)}
              </Message>
              {!isLoadingAccums && memberAccumulations?.accumulations && formatDollars(memberAccumulations.accumulations)}
            </LoadingGroup.Placeholder>
          </LoadingGroup>
        </Stack>
        <Shelf.Spacer />
      </Shelf>
      <Shelf>
        <Stack spacing="mini">
          <LoadingGroup loading={isTotalTransactionsLoading}>
            <LoadingGroup.Placeholder
              fallback={
                <Stack spacing="mini">
                  <LoadingSkeleton style={{ width: 75, height: 20 }} />
                  <LoadingSkeleton style={{ width: 150, height: 20 }} />
                </Stack>
              }
            >
              <Message visible={isTotalTransactionsError && isLoggedInUser} appearance={Messages.Error}>
                {translate(`${TRANSLATION_PATH}.noPaymentInformationLoaded`)}
              </Message>
              {!isTotalTransactionsError && !isTotalTransactionsLoading && (
                <Stack spacing="none">
                  <Semibold>
                    {translate(`${TRANSLATION_PATH}.paid`)} ({translate(`${TRANSLATION_PATH}.asOf`)}{" "}
                    {transactionsTotal?.latest_transaction_date && formatDate(transactionsTotal.latest_transaction_date, "MM/DD/YYYY")}):
                  </Semibold>
                  {transactionsTotal?.total_balance && formatDollars(transactionsTotal.total_balance?.toString())}
                </Stack>
              )}
            </LoadingGroup.Placeholder>
          </LoadingGroup>
        </Stack>
        <Shelf.Spacer />
      </Shelf>
      {!hideMemberClaimsPage && (
        <Body textAlign="left">
          {translate(`${TRANSLATION_PATH}.howIsThisCalculated`)}{" "}
          <ClientAwareLink testId="claim-history-link" to="/claims-history">
            {translate(`${TRANSLATION_PATH}.viewChargedClaims`)}
          </ClientAwareLink>
        </Body>
      )}
    </Stack>
  );
};
